import { createContext, useCallback, useContext, useEffect, useRef, useState, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Session, User } from '@supabase/supabase-js';
import { supabase } from 'src/services/supabase/client';
import { Tables } from 'src/types/db.types';
import { PermissionKey, AppRole } from 'src/types/permissions.types';
import {
  getRolePermissions,
  hasRolePermission,
  hasAnyPermission,
  getRoleBaseRoute,
  canPerformAction,
  setRoleDefinitions,
} from 'src/config/permissions';
import { roleService } from 'src/services/role.service';

// Constants
export const STORAGE_KEY = 'goldminds_auth_user';

// Types
interface DBUser {
  id: string;
  role: string;
  firstname?: string;
  lastname?: string;
  email?: string;
  displayName?: string;
}

interface AuthUser extends User {
  dbUser?: Tables<'users'>;
}

interface AuthState {
  user: AuthUser | null;
  session: Session | null;
  isLoading: boolean;
  error: Error | null;
}

interface AuthContextType extends AuthState {
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  updateUser: (user: Partial<AuthUser>) => void;
  isAuthenticated: boolean;
  userRole?: string;
  permissions: PermissionKey[];
  hasPermission: (permission: PermissionKey | string) => boolean;
  hasAnyPermission: (permissions: Array<PermissionKey | string>) => boolean;
  can: (action: string, resource?: string) => boolean;
  getBaseRoute: (subpath?: string) => string;
  isRole: (role: AppRole | AppRole[] | string | string[]) => boolean;
  /** Bumps each time role definitions reload; use as a memo dependency. */
  rolesVersion: number;
  /** Reloads role definitions from the `roles` table. */
  refreshRoles: () => Promise<void>;
}

// Create Context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Initial State
const initialState: AuthState = {
  user: null,
  session: null,
  isLoading: false,
  error: null,
};

// Helper Functions
function getUserFromStorage(): AuthUser | null {
  try {
    const storedUser = localStorage.getItem(STORAGE_KEY);
    return storedUser ? JSON.parse(storedUser) : null;
  } catch (error) {
    console.error('Error reading from localStorage:', error);
    return null;
  }
}

function setUserInStorage(user: AuthUser | null): void {
  try {
    if (user) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(user));
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  } catch (error) {
    console.error('Error writing to localStorage:', error);
  }
}

async function fetchUserFromDB(userId: string): Promise<DBUser | null> {
  try {
    const { data, error } = await supabase.from('users').select('*').eq('id', userId).single();

    if (error) throw error;
    return data as DBUser;
  } catch (error) {
    console.error('Error fetching user from DB:', error);
    return null;
  }
}

// Provider Component
export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<AuthState>(initialState);

  // Update state helper
  const updateState = (updates: Partial<AuthState>) => {
    console.log('🔄 AuthContext State Update:', {
      updates,
      previousState: {
        isLoading: state.isLoading,
        isAuthenticated: !!state.user,
        userRole: state.user?.role,
        hasSession: !!state.session,
      },
      timestamp: new Date().toISOString(),
    });
    setState((prev) => ({ ...prev, ...updates }));
  };

  useEffect(() => {
    const initializeAuth = async () => {
      console.log('🚀 Initializing Auth Context...');
      try {
        const {
          data: { session },
          error: sessionError,
        } = await supabase.auth.getSession();
        if (sessionError) throw sessionError;

        const storedUser = getUserFromStorage();

        console.log('🔍 Auth Initialization Data:', {
          hasSession: !!session,
          sessionUserId: session?.user?.id,
          hasStoredUser: !!storedUser,
          storedUserId: storedUser?.id,
          storedUserRole: storedUser?.role,
          timestamp: new Date().toISOString(),
        });

        // If no session and no stored user (logged out state), we're not loading
        const isLoggedOut = !session && !storedUser;

        if (isLoggedOut) {
          console.log('📤 User is logged out, setting initial state');
          updateState({
            session: null,
            user: null,
            isLoading: false,
          });
          return;
        }

        // If we have a session, always start with loading true
        // Let the user profile fetch effect handle setting loading to false
        console.log('📥 User has session, starting with loading state');
        updateState({
          session,
          user: storedUser,
          isLoading: true, // Always load when we have a session
        });
      } catch (error) {
        console.error('❌ Error initializing auth:', error);
        // If we get an invalid refresh token error, clear everything and let user re-login
        const errorMessage = (error as Error)?.message || '';
        if (errorMessage.includes('Invalid Refresh Token') || errorMessage.includes('Refresh Token')) {
          console.log('🔒 Invalid refresh token detected, clearing auth state');
          setUserInStorage(null);
          updateState({
            session: null,
            user: null,
            isLoading: false,
            error: new Error('Session expired. Please sign in again.'),
          });
          return;
        }
        updateState({ error: error as Error, isLoading: false });
      }
    };

    initializeAuth();
  }, []);

  // Handle auth state changes
  useEffect(() => {
    const handleAuthChange = (_event: string, session: Session | null) => {
      console.log('🎧 Supabase Auth State Change:', {
        event: _event,
        hasSession: !!session,
        sessionUserId: session?.user?.id,
        timestamp: new Date().toISOString(),
      });

      if (_event === 'SIGNED_OUT') {
        console.log('👋 User signed out, clearing auth state');
        // Clear state on sign out
        setUserInStorage(null);
        updateState({
          session: null,
          user: null,
          isLoading: false,
        });
        return;
      }

      // Handle token refresh failure - this happens when refresh token is invalid/expired
      if (_event === 'TOKEN_REFRESHED' && !session) {
        console.log('🔄 Token refresh failed, clearing auth state');
        setUserInStorage(null);
        updateState({
          session: null,
          user: null,
          isLoading: false,
          error: new Error('Session expired. Please sign in again.'),
        });
        return;
      }

      console.log('📝 Updating session in auth state');
      updateState({ session });
    };

    const {
      data: { subscription: authListener },
    } = supabase.auth.onAuthStateChange(handleAuthChange);

    return () => authListener.unsubscribe();
  }, []);

  // Handle user profile updates separately
  useEffect(() => {
    const fetchUserProfile = async () => {
      const { session } = state;

      console.log('👤 User Profile Fetch Check:', {
        hasSession: !!session,
        sessionUserId: session?.user?.id,
        currentUserId: state.user?.id,
        isLoading: state.isLoading,
        timestamp: new Date().toISOString(),
      });

      if (!session?.user?.id) {
        console.log('❌ No session or user ID, setting loading to false');
        updateState({ isLoading: false });
        return;
      }

      // A cached user renders immediately, but the profile is still re-read so a
      // role an admin changed takes effect on the next page load, not the next login.
      const hasCachedUser = state.user?.id === session.user.id;
      if (hasCachedUser) {
        console.log('✅ User data already exists for current session, refreshing in background');
        updateState({ isLoading: false });
      }

      try {
        console.log('🔍 Fetching user profile from database for ID:', session.user.id);
        if (!hasCachedUser) updateState({ isLoading: true });
        const { data: dbUser, error } = await supabase.from('users').select('*').eq('id', session.user.id).single();

        if (error) throw error;

        if (dbUser) {
          console.log('✅ User profile fetched successfully:', {
            userId: dbUser.id,
            role: dbUser.role,
            name: `${dbUser.firstname} ${dbUser.lastname}`,
          });

          const enrichedUser = {
            ...session.user,
            dbUser,
            role: dbUser.role,
            firstname: dbUser.firstname,
            lastname: dbUser.lastname,
            email: dbUser.email,
            displayName: `${dbUser.firstname} ${dbUser.lastname}`,
          };

          setUserInStorage(enrichedUser);
          updateState({ user: enrichedUser, isLoading: false });
        }
      } catch (error) {
        console.error('❌ Error fetching user profile:', error);
        // A failed background refresh keeps the cached user rather than signing them out.
        if (!hasCachedUser) updateState({ error: error as Error, isLoading: false });
      }
    };

    fetchUserProfile();
  }, [state.session?.user?.id]); // Only re-run when session user ID changes

  // Auth methods
  const signIn = async (email: string, password: string) => {
    try {
      updateState({ isLoading: true, error: null });
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;
    } catch (error) {
      console.error('Sign in error:', error);
      updateState({ error: error as Error, isLoading: false });
      throw error;
    }
  };

  const signOut = async () => {
    try {
      updateState({ isLoading: true, error: null });
      const { error } = await supabase.auth.signOut({ scope: 'local' });
      if (error) throw error;

      // Clear state and storage
      setUserInStorage(null);
      updateState({
        session: null,
        user: null,
        isLoading: false,
      });
    } catch (error) {
      console.error('Sign out error:', error);
      updateState({ error: error as Error });
      throw error;
    } finally {
      updateState({ isLoading: false });
    }
  };

  const updateUser = (updates: Partial<AuthUser>) => {
    if (!state.user) return;
    const updatedUser = { ...state.user, ...updates };
    setUserInStorage(updatedUser);
    updateState({ user: updatedUser });
  };

  // Role definitions live in the `roles` table. Hold the app in its loading
  // state until they arrive, so a custom role is never treated as unknown.
  const [rolesVersion, setRolesVersion] = useState(0);
  const [rolesReady, setRolesReady] = useState(false);
  const sessionUserId = state.session?.user?.id;

  const refreshRoles = useCallback(async () => {
    try {
      setRoleDefinitions(await roleService.getRoles());
    } catch (error) {
      console.error('Failed to load roles, using built-in roles only:', error);
    }
    setRolesVersion((v) => v + 1);
    setRolesReady(true);
  }, []);

  useEffect(() => {
    if (!sessionUserId) {
      setRolesReady(true);
      return;
    }
    setRolesReady(false);
    refreshRoles();
  }, [sessionUserId, refreshRoles]);

  const userRole = state.user?.role;

  // Cached queries (scholar lists, balances…) belong to one person with one role.
  // Drop them when either changes, so nobody sees data fetched under another identity.
  const queryClient = useQueryClient();
  const identity = state.user ? `${state.user.id}:${userRole}` : '';
  const previousIdentity = useRef(identity);
  useEffect(() => {
    if (previousIdentity.current === identity) return;
    // Nothing to drop when nobody was signed in; sign-out already cleared it.
    if (previousIdentity.current) queryClient.clear();
    previousIdentity.current = identity;
  }, [identity, queryClient]);

  const permissions = useMemo(() => {
    return getRolePermissions(userRole);
  }, [userRole, rolesVersion]);

  const hasPermission = (permission: PermissionKey | string) => {
    return hasRolePermission(userRole, permission);
  };

  const hasAny = (required: Array<PermissionKey | string>) => {
    return hasAnyPermission(userRole, required);
  };

  const can = (action: string, resource?: string) => {
    return canPerformAction(userRole, action, resource);
  };

  const getBaseRoute = (subpath?: string) => {
    return getRoleBaseRoute(userRole, subpath);
  };

  const isRole = (role: AppRole | AppRole[] | string | string[]) => {
    if (!userRole) return false;
    const current = userRole.toUpperCase().trim();
    if (Array.isArray(role)) {
      return role.some((r) => r.toUpperCase().trim() === current);
    }
    return role.toUpperCase().trim() === current;
  };

  const value = {
    ...state,
    isLoading: state.isLoading || !rolesReady,
    signIn,
    isAuthenticated: !!state.user,
    signOut,
    updateUser,
    userRole,
    permissions,
    hasPermission,
    hasAnyPermission: hasAny,
    can,
    getBaseRoute,
    isRole,
    rolesVersion,
    refreshRoles,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// Custom Hook
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
